import { Subject } from 'rxjs';

import { Dialog, DialogConfig, CdkDialogContainer } from '@angular/cdk/dialog';
import { Overlay, ComponentType, ScrollStrategy, createGlobalPositionStrategy } from '@angular/cdk/overlay';
import { Type, inject, Injector, OnDestroy, Injectable, ComponentRef, InjectionToken } from '@angular/core';

import { DialogBase } from './dialog-base';
import { AppDialogRef } from './dialog-ref';
import { AppDialogConfig } from './dialog-config';

export const APP_DIALOG_DATA = new InjectionToken<any>('AppDialogData');

export const APP_DIALOG_DEFAULT_OPTIONS = new InjectionToken<AppDialogConfig>('app-dialog-default-options');

export const APP_DIALOG_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>('app-dialog-scroll-strategy', {
  providedIn: 'root',
  factory: () => {
    const overlay = inject(Overlay);
    return () => overlay.scrollStrategies.block();
  },
});

export function APP_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: Overlay): () => ScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

export const APP_DIALOG_SCROLL_STRATEGY_PROVIDER = {
  provide: APP_DIALOG_SCROLL_STRATEGY,
  deps: [Overlay],
  useFactory: APP_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
};

let uniqueId = 0;

@Injectable({ providedIn: 'root' })
export class DialogService implements OnDestroy {
  private readonly dialog = inject(Dialog);
  private readonly injector = inject(Injector);
  private readonly parentDialog = inject(DialogService, { optional: true, skipSelf: true });
  private readonly scrollStrategy = inject(APP_DIALOG_SCROLL_STRATEGY);
  private readonly defaultOptions = inject(APP_DIALOG_DEFAULT_OPTIONS, { optional: true });

  private readonly openDialogsAtThisLevel: AppDialogRef<any>[] = [];
  private readonly afterAllClosedAtThisLevel = new Subject<void>();
  protected dialogConfigClass = AppDialogConfig;

  private readonly dialogRefConstructor: Type<AppDialogRef<any>>;
  private readonly dialogContainerType: Type<CdkDialogContainer>;
  private readonly dialogDataToken: InjectionToken<any>;

  constructor() {
    this.dialogRefConstructor = AppDialogRef;
    this.dialogContainerType = CdkDialogContainer;
    this.dialogDataToken = APP_DIALOG_DATA;
  }

  get openDialogs(): AppDialogRef<any>[] {
    return this.parentDialog ? this.parentDialog.openDialogs : this.openDialogsAtThisLevel;
  }

  open<DialogData, DialogResult>(
    component: ComponentType<DialogBase<DialogData, DialogResult>>,
    config?: AppDialogConfig<DialogData>,
  ): AppDialogRef<DialogBase<DialogData, DialogResult>, DialogResult> {
    let dialogRef: AppDialogRef<DialogBase<DialogData, DialogResult>, DialogResult>;

    config = { ...(this.defaultOptions || new AppDialogConfig()), ...config };
    config.id = config.id || `dialog-${uniqueId++}`;
    config.scrollStrategy = config.scrollStrategy || this.scrollStrategy();

    const cdkRef = this.dialog.open<DialogResult, DialogData, DialogBase<DialogData, DialogResult>>(component, {
      ...config,
      panelClass: [
        'dialog-panel',
        ...(config?.panelClass ? (Array.isArray(config.panelClass) ? config.panelClass : [config.panelClass]) : []),
      ],
      positionStrategy: createGlobalPositionStrategy(this.injector).centerHorizontally().centerVertically(),
      disableClose: true,
      closeOnDestroy: false,
      closeOnOverlayDetachments: false,
      container: {
        type: this.dialogContainerType,
        providers: () => [
          { provide: this.dialogConfigClass, useValue: config },
          { provide: DialogConfig, useValue: config },
        ],
      },
      templateContext: () => ({ dialogRef }),
      providers: (ref, cdkConfig) => {
        dialogRef = new this.dialogRefConstructor(ref, config);
        dialogRef.updatePosition(config?.position);
        return [
          { provide: this.dialogDataToken, useValue: cdkConfig.data },
          { provide: this.dialogRefConstructor, useValue: dialogRef },
        ];
      },
    });

    (dialogRef! as { componentRef: ComponentRef<DialogBase<DialogData, DialogResult>> }).componentRef =
      cdkRef.componentRef!;
    dialogRef!.componentInstance = cdkRef.componentInstance!;

    this.openDialogs.push(dialogRef!);

    return dialogRef!;
  }

  openFullScreen<DialogData, DialogResult>(
    component: ComponentType<DialogBase<DialogData, DialogResult>>,
    config?: AppDialogConfig<DialogData>,
  ): AppDialogRef<DialogBase<DialogData, DialogResult>, DialogResult> {
    return this.open(component, {
      ...config,
      panelClass: [
        'dialog-full-screen-panel',
        ...(config?.panelClass ? (Array.isArray(config.panelClass) ? config.panelClass : [config.panelClass]) : []),
      ],
    });
  }

  closeAll(): void {
    this._closeDialogs(this.openDialogs);
  }

  getDialogById(id: string): AppDialogRef<any> | undefined {
    return this.openDialogs.find((dialog) => dialog.id === id);
  }

  openSlideRight<DialogData, DialogResult>(
    component: ComponentType<DialogBase<DialogData, DialogResult>>,
    config?: AppDialogConfig<DialogData>,
  ): AppDialogRef<DialogBase<DialogData, DialogResult>, DialogResult> {
    const slideConfig: AppDialogConfig<DialogData> = {
      ...config,
      panelClass: [
        'dialog-panel-slide-right',
        ...(config?.panelClass ? (Array.isArray(config.panelClass) ? config.panelClass : [config.panelClass]) : []),
      ],
      backdropClass: [
        'dialog-backdrop-slide',
        ...(config?.backdropClass
          ? Array.isArray(config.backdropClass)
            ? config.backdropClass
            : [config.backdropClass]
          : []),
      ],
      hasBackdrop: true,
      maxWidth: config?.maxWidth || '500px',
      height: config?.height || '100vh',
    };

    let dialogRef: AppDialogRef<DialogBase<DialogData, DialogResult>, DialogResult>;

    slideConfig.id = slideConfig.id || `dialog-${uniqueId++}`;
    slideConfig.scrollStrategy = slideConfig.scrollStrategy || this.scrollStrategy();

    const cdkRef = this.dialog.open<DialogResult, DialogData, DialogBase<DialogData, DialogResult>>(component, {
      ...slideConfig,
      positionStrategy: createGlobalPositionStrategy(this.injector).right('0').top('0'),
      disableClose: true,
      container: {
        type: this.dialogContainerType,
        providers: () => [
          { provide: this.dialogConfigClass, useValue: slideConfig },
          { provide: DialogConfig, useValue: slideConfig },
        ],
      },
      templateContext: () => ({ dialogRef }),
      providers: (ref, cdkConfig) => {
        dialogRef = new this.dialogRefConstructor(ref, slideConfig);
        dialogRef.updatePosition(slideConfig?.position);

        const originalClose = dialogRef.close.bind(dialogRef);
        dialogRef.close = (result?: DialogResult) => {
          ref.overlayRef.overlayElement.classList.add('dialog-exiting');
          ref.overlayRef.backdropElement?.classList.add('backdrop-exiting');
          setTimeout(() => originalClose(result), 500);
        };

        return [
          { provide: this.dialogDataToken, useValue: cdkConfig.data },
          { provide: this.dialogRefConstructor, useValue: dialogRef },
        ];
      },
    });

    (dialogRef! as { componentRef: ComponentRef<DialogBase<DialogData, DialogResult>> }).componentRef =
      cdkRef.componentRef!;
    dialogRef!.componentInstance = cdkRef.componentInstance!;

    this.openDialogs.push(dialogRef!);

    return dialogRef!;
  }

  ngOnDestroy(): void {
    this._closeDialogs(this.openDialogsAtThisLevel);
    this.afterAllClosedAtThisLevel.complete();
  }

  private _closeDialogs(dialogs: AppDialogRef<any>[]): void {
    let i = dialogs.length;

    while (i--) {
      dialogs[i].close();
    }
  }
}
